import React, { useState, useEffect, useRef } from "react";
import { Box, Button, Typography, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { BoxProps, styled } from "@mui/system";
import GroupIcon from "../../Assets/images/svg/Icons/Group 578.svg";
import { GetApiMethod, PatchApiMethod, PostApiMethod } from "../../Utils/ApiService";
import SuccessDialog, { useSuccessDialog } from "../../Components/Common/SuccesMessages/SuccessDialog";
import photo from "../../Assets/images/svg/Icons/file-image-svgrepo-com.svg";
import { useNavigate } from "react-router-dom";

interface BannerUploadFormProps {
  formData: any;
  setFormData: (data: any) => void;
  handleBack?: () => void;
  onSubmit?: () => void;
}

const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

const UploadBox = styled(Box)<BoxProps>(() => ({
  border: "2px dashed #ccc",
  borderRadius: "10px",
  padding: "20px",
  textAlign: "center",
  cursor: "pointer",
  width: "100%",
  height: 150,
}));

const BannerUploadForm: React.FC<BannerUploadFormProps> = ({
  formData,
  setFormData,
  handleBack,
  onSubmit,
}) => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { open: successOpen, message, showSuccess, closeSuccess } = useSuccessDialog();

  // Load banner data for edit mode
  useEffect(() => {
    const fetchBannerById = async () => {
      if (formData?.id) {
        try {
          const response = await GetApiMethod(`banner/${formData.id}`);
          const bannerData = response?.data?.data?.banner;
          if (bannerData) {
            // Use imageUrl if available, else fallback to constructing from fileKey
            const imageUrl = bannerData.imageUrl
              ? bannerData.imageUrl
              : `${baseUrl}/${bannerData.fileKey}`;

            setPreviewUrl(imageUrl);
            setFormData(bannerData);
          }
        } catch (error) {
          console.error("Error fetching banner:", error);
        }
      }
    };

    fetchBannerById();
  }, [formData?.id, setFormData]);

  // Handle file input change
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      setFormData((prev: any) => ({
        ...prev,
        fileName: file.name,
      }));
    }
  };

  // Save or update banner
  const handleSave = async () => {
    if (!selectedFile && !formData?.id) {
      alert("Please upload a banner image.");
      return;
    }

    try {
      setLoading(true);
      const form = new FormData();

      if (selectedFile) {
        form.append("files", selectedFile);
        form.append("fileName", selectedFile.name);
      } else {
        form.append("fileName", formData.fileName);
      }

      let response;
      if (formData?.id) {
        response = await PatchApiMethod(`banner/${formData.id}`, form);
      } else {
        response = await PostApiMethod(`banner`, form);
      }

      if (response?.data?.status === "success") {
        const banner = response?.data?.data?.banner;

        showSuccess(formData?.id ? "Banner Updated successfully!" : "Banner Added successfully!");

        if (banner) {
          setFormData(banner);
          setPreviewUrl(banner.imageUrl ? banner.imageUrl : `${baseUrl}/${banner.fileKey}`);
        }

        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }

        if (onSubmit) onSubmit();

        // Navigate to /banners (remove ID param from URL)
        navigate("/banners");
      } else {
        alert(response?.data?.message || "Something went wrong!");
      }
    } catch (error) {
      console.error("Error saving banner:", error);
      alert("Failed to save banner. Try again.");
    } finally {
      setLoading(false);
    }
  };

  // Clear preview and selected file
  const handleClear = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setFormData((prev: any) => ({
      ...prev,
      fileName: "",
    }));

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <Box>
      <Typography className="form-input" mt={3}>
        Banner Image
      </Typography>

      <UploadBox component="label">
        <img src={photo} alt="Upload icon" style={{ marginTop: 15, width: 40 }} />
        <Typography fontSize="14px" mt={1}>
          Drag & drop a file or{" "}
          <span style={{ color: "#3E93E8", cursor: "pointer" }}>Choose file</span>
        </Typography>
        <input
          ref={fileInputRef}
          type="file"
          hidden
          accept="image/*"
          onChange={handleFileChange}
        />
      </UploadBox>

      {previewUrl && (
        <Box mt={2} position="relative" display="inline-block">
          <img
            src={previewUrl}
            alt={formData.fileName || selectedFile?.name}
            style={{ width: 200, height: 120, objectFit: "cover", borderRadius: 8 }}
          />
          <Typography mt={1} fontSize={14} textAlign="center">
            {selectedFile?.name || formData.fileName}
          </Typography>
          <IconButton
            size="small"
            onClick={handleClear}
            sx={{
              position: "absolute",
              top: "-10px",
              right: "-10px",
              backgroundColor: "white",
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      <SuccessDialog open={successOpen} onClose={closeSuccess} message={message} />

      <Box display="flex" justifyContent="start" gap={2} mt={3}>
        {handleBack && (
          <Button onClick={handleBack} className="btn-back">
            <Box component="img" src={GroupIcon} sx={{ mr: 1 }} />
            Back
          </Button>
        )}
        <Button onClick={handleSave} className="btn-next" disabled={loading}>
          {loading ? "Saving..." : formData?.id ? "Update" : "Add"}
        </Button>
      </Box>
    </Box>
  );
};

export default BannerUploadForm;
